import { useCallback, useEffect, useState } from "react";
import { growthApi } from "../api/endpoints/growth";

/**
 * CRUD wrapper around `/api/v1/growth/{childId}`.
 */
export function useGrowth(childId) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(Boolean(childId));
    const [error, setError] = useState(null);

    const refetch = useCallback(async () => {
        if (!childId) {
            setRecords([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await growthApi.list(childId);
            setRecords(data);
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [childId]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    const create = useCallback(
        async (record) => {
            const created = await growthApi.create(childId, record);
            setRecords((prev) => [...prev, created]);
            return created;
        },
        [childId]
    );

    const update = useCallback(
        async (recordId, patch) => {
            const updated = await growthApi.update(childId, recordId, patch);
            setRecords((prev) => prev.map((r) => (r.id === recordId ? updated : r)));
            return updated;
        },
        [childId]
    );

    const remove = useCallback(
        async (recordId) => {
            await growthApi.remove(childId, recordId);
            setRecords((prev) => prev.filter((r) => r.id !== recordId));
        },
        [childId]
    );

    return { records, loading, error, refetch, create, update, remove };
}
